import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Dropdown, Form, Grid, Icon, Input, Modal, Popup, Segment } from 'semantic-ui-react'
import { Field, Form as FinalForm } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import CopyToClipboard from 'react-copy-to-clipboard'
import { toast } from 'react-toastify'

import { LookProductApiResponse } from '../../redux/types/products'
import { LookupValues } from '../../redux/types/ui'
import { setProducts, getProducts, getAProduct, getBrands } from '../../redux/actions/products'
import {
  setIsLoading,
  setDirectUrl,
  resetLookup,
  setBatchLookupStage,
  setLookupValues,
  setEnableDeepMatcher,
  setSearchFunc,
} from '../../redux/actions/ui'
import { saveLocal } from '../../utils/localStorage'
import { AppState } from '../../redux/store'
import { LookupTable } from '../LookupTable/LookupTable'
import { CustomCopyButton } from './styles'
import { ProductModalStyle } from '../Products/styles'
import {
  API_PRODUCTS,
  LINEAGE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  PRODUCT_SUBTYPE_OPTIONS,
  PRODUCT_TYPE_KEY_BY_ID,
  AbbreviatedProductContainer,
  AbbreviatedProduct,
} from '../../consts'
import { IdSelectionOptions, SelectionOptions } from '../SelectionOptions/SelectionOptions'
import { TableCellEdit } from './TableCellEdit'
import { TableCellCreate } from './TableCellCreate'
import { TableCellProductAlias } from './TableCellProductAlias'
import { TableCellCopy } from './TableCellCopy'
import { ProductClipboard } from './ProductClipboard'
import { LookupOptionsFormGroup } from './LookupOptionsFormGroup'
import { photoDownloader } from '../../utils/photoDownloader'
import { productAttributesLinter } from '../../utils/productAttributesLinter'
import Products from '../Products/Products'
import { serializeLookupValues } from '../../utils/lookupValues'
import { displayBrandAndStatus } from '../../utils/Brands'
import { AlgoliaAutoComplete } from '../AlgoliaAutoComplete/AlgoliaAutoComplete'
import { FormApi } from 'final-form'
import { TableCellCheckbox } from './TableCellCheckbox'

export type LookupProductMode = 'default' | 'bulkEdit' | 'batchLookup'

interface Props {
  addProductToBulkEditProductsQueue?: (
    abbrevProduct: AbbreviatedProduct | AbbreviatedProduct[]
  ) => void
  bulkEditProductsQueue?: AbbreviatedProductContainer
  customColumns?: any[]
  mode?: LookupProductMode
  onModalActiveChange: (val: boolean) => void
  preSelectedId?: number | undefined | null
  removeProductFromBulkEditProductsQueue?: (id: number) => void
}

const LookupProduct = (props: Props) => {
  const {
    mode = 'default',
    bulkEditProductsQueue,
    addProductToBulkEditProductsQueue,
    removeProductFromBulkEditProductsQueue,
    preSelectedId,
  } = props
  const isBatchLookupMode = mode === 'batchLookup'
  const isBulkEditMode = mode === 'bulkEdit'
  if (
    isBulkEditMode &&
    (!addProductToBulkEditProductsQueue ||
      !removeProductFromBulkEditProductsQueue ||
      !bulkEditProductsQueue)
  ) {
    throw new Error(
      'When LookupProduct is in bulkEdit mode, you must supply the props addProductToBulkEditProductsQueue, removeProductFromBulkEditProductsQueue, and bulkEditProductsQueue'
    )
  }

  const dispatch = useDispatch()
  const [copied, setCopied] = useState(false)
  const [isDirectLookup, setIsDirectLookup] = useState(false)
  const [hideQueuedProducts, setHideQueuedProducts] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const { aliasesById } = useSelector((state: AppState) => state.productAliases)
  const { brands, products, productPhotos } = useSelector((state: AppState) => state.products)
  const brandSearchMode = useSelector((state: AppState) => state.products.brandSearchMode)
  const submitButtonRef = useRef(null)

  const focusOnSubmitButton = () => {
    if (submitButtonRef && submitButtonRef.current) {
      // @ts-ignore
      submitButtonRef.current.focus()
    }
  }

  const hidingQueuedProducts = useMemo(
    () => hideQueuedProducts && isBulkEditMode && bulkEditProductsQueue,
    [bulkEditProductsQueue, hideQueuedProducts, isBulkEditMode]
  )

  const {
    lookupValues,
    lookupProductId,
    isLoading,
    directUrl,
    gettingProductId,
    lastAction,
    clipboardProduct,
    batchLookupValues,
    currentBatchId,
    searchFunc,
    enableDeepMatcher,
    batchLookupStage,
  } = useSelector((state: AppState) => state.ui)
  const pjson = require('../../../package.json')
  const directUrlHost =
    process.env.NODE_ENV === 'development' ? pjson.proxy.split('//')[1] : window.location.host
  const isSql = searchFunc === 'sql'

  useEffect(() => {
    dispatch(getBrands())
  }, [dispatch])

  const onSubmit = async (values: LookupValues) => {
    if (!values.name && !values.brand && process.env.NODE_ENV !== 'test') {
      toast('Specify a name or brand', {
        containerId: 'error',
      })
      return
    }

    dispatch(setProducts({ products: [] }))
    const newFormData = serializeLookupValues(
      values,
      searchFunc,
      brandSearchMode,
      isBatchLookupMode,
      enableDeepMatcher
    )

    dispatch(
      setDirectUrl({
        url: `//${directUrlHost}${API_PRODUCTS}?${new URLSearchParams(
          newFormData as {}
        ).toString()}`,
      })
    )

    dispatch(setLookupValues({ values: values }))
    dispatch(setIsLoading({ status: true }))
    dispatch(
      getProducts({ values, searchFunc, brandSearchMode, isBatchLookupMode, enableDeepMatcher })
    )
  }

  const onDownloadClick = useCallback(
    async (row: any) => {
      const url = productPhotos[row.values.id]?.[0]?.['urls']?.['original']
      const fileName = row.original.attributes.name
      await photoDownloader(url, fileName)
    },
    [productPhotos]
  )

  const getImageUrls = useCallback(
    (productId: number) => {
      if (productId && productPhotos[productId]) {
        return [...productPhotos[productId]]
          .sort((eachPhoto: any) => eachPhoto.position)
          .map((eachPhoto: any) => eachPhoto.id)
      }
      return []
    },
    [productPhotos]
  )

  const bulkEditSelectAll = useCallback(() => {
    // @ts-ignore
    addProductToBulkEditProductsQueue(
      products.map(
        (product) =>
          ({
            id: product.id,
            name: product.attributes.name,
            brand: product.attributes.brand,
            brand_subtype: product.attributes.brand_subtype,
            product_type: product.attributes.product_type,
            lineage:
              LINEAGE_OPTIONS.find((option) => option.id === product.attributes.lineage)?.value ||
              '',
            image_urls: getImageUrls(product.id),
          } as AbbreviatedProduct)
      )
    )
  }, [addProductToBulkEditProductsQueue, getImageUrls, products])

  const brandDetails = useCallback(
    (id: number | null) => {
      if (id === null) {
        return
      }

      return brands.find((b) => b.id === id)
    },
    [brands]
  )

  const bulkEditLookupColumns = React.useMemo(
    () =>
      isBulkEditMode
        ? [
            {
              Header: products.length ? (
                <Button size="mini" secondary onClick={() => bulkEditSelectAll()}>
                  Add all
                </Button>
              ) : (
                ''
              ),
              id: 'Queue',
              Cell: ({ cell: { value, row } }: { cell: { value: any; row: any } }) => {
                return (
                  <TableCellCheckbox
                    selected={bulkEditProductsQueue && !!bulkEditProductsQueue[row.values.id]}
                    onAdd={addProductToBulkEditProductsQueue}
                    onRemove={removeProductFromBulkEditProductsQueue}
                    row={{ ...row, image_urls: getImageUrls(row.values.id) }}
                  />
                )
              },
            },
            {
              Header: 'ID',
              accessor: 'id',
              Cell: ({ cell: { value, row } }: { cell: { value: string[]; row: any } }) => {
                const url = productPhotos[row.values.id]?.[0]?.['urls']?.['medium'] || ''
                return url ? (
                  <Popup
                    trigger={
                      <div style={{ minWidth: 70 }}>
                        <a href={row.original.attributes.url} target="new">
                          {value}
                        </a>
                        <Icon
                          link
                          color="grey"
                          name="file image outline"
                          style={{ paddingLeft: 10 }}
                          onClick={() => onDownloadClick(row)}
                        />
                      </div>
                    }
                    hoverable
                    position="bottom left"
                    mouseEnterDelay={300}
                  >
                    <div style={{ width: 200, height: 200 }}>
                      <img src={url} className="ui image" alt="product" />
                    </div>
                  </Popup>
                ) : (
                  <div style={{ minWidth: 70 }}>
                    <a href={row.original.attributes.url} target="new">
                      {value}
                    </a>
                  </div>
                )
              },
            },
            {
              Header: 'Name',
              accessor: 'attributes.name',
            },
            {
              Header: 'Brand',
              // using 'any' here because the structure 'p' is a larger object populated by
              // the table library we are using, which is not in typescript and so does not
              // export types for us to use here.
              accessor: (p: any) => {
                const brand = brandDetails(p.attributes.brand_id)
                if (!brand) {
                  return p.attributes.brand
                } else {
                  return displayBrandAndStatus(brand)
                }
              },
            },
            {
              Header: 'Brand subtype',
              accessor: 'attributes.brand_subtype',
            },
            {
              Header: 'Lineage',
              accessor: 'attributes.lineage',
            },
            {
              Header: 'Product type',
              accessor: 'attributes.product_type',
            },
            {
              Header: 'Product subtype',
              accessor: 'attributes.product_subtype',
            },
            {
              Header: 'Amount',
              accessor: 'attributes.amount',
            },
            {
              Header: 'Dosage',
              accessor: 'attributes.dosage',
            },
          ]
        : [],
    [
      isBulkEditMode,
      products.length,
      bulkEditSelectAll,
      bulkEditProductsQueue,
      addProductToBulkEditProductsQueue,
      getImageUrls,
      removeProductFromBulkEditProductsQueue,
      productPhotos,
      onDownloadClick,
      brandDetails,
    ]
  )

  // useMemo is needed for react-table
  // [ref] https://github.com/tannerlinsley/react-table/blob/master/docs/api.md
  const defaultAndBatchLookupColumns = React.useMemo(() => {
    if (isBulkEditMode) return []

    return [
      {
        Header: 'ID',
        accessor: 'id',
        Cell: ({ cell: { value, row } }: { cell: { value: string[]; row: any } }) => {
          const url = productPhotos[row.values.id]?.[0]?.['urls']?.['medium'] || ''
          return url ? (
            <Popup
              trigger={
                <div style={{ minWidth: 70 }}>
                  <a href={row.original.attributes.url} target="new">
                    {value}
                  </a>
                  <Icon
                    link
                    color="grey"
                    name="file image outline"
                    style={{ paddingLeft: 10 }}
                    onClick={() => onDownloadClick(row)}
                  />
                </div>
              }
              hoverable
              position="bottom left"
              mouseEnterDelay={300}
            >
              <div style={{ width: 200, height: 200 }}>
                <img src={url} className="ui image" alt="product" />
              </div>
            </Popup>
          ) : (
            <div style={{ minWidth: 70 }}>
              <a href={row.original.attributes.url} target="new">
                {value}
              </a>
            </div>
          )
        },
      },
      {
        Header: 'Score',
        accessor: 'score',
      },
      {
        Header: 'Name',
        accessor: 'attributes.name',
      },
      {
        Header: 'Brand',
        // using 'any' here because the structure 'p' is a larger object populated by
        // the table library we are using, which is not in typescript and so does not
        // export types for us to use here.
        accessor: (p: any) => {
          const brand = brandDetails(p.attributes.brand_id)
          if (!brand) {
            return p.attributes.brand
          } else {
            return displayBrandAndStatus(brand)
          }
        },
      },
      {
        Header: 'Brand subtype',
        accessor: 'attributes.brand_subtype',
      },
      {
        Header: 'Lineage',
        accessor: 'attributes.lineage',
      },
      {
        Header: 'Product type',
        accessor: 'attributes.product_type',
      },
      {
        Header: 'Product subtype',
        accessor: 'attributes.product_subtype',
      },
      {
        Header: 'Amount',
        accessor: 'attributes.amount',
      },
      {
        Header: 'Dosage',
        accessor: 'attributes.dosage',
      },
      {
        Header: isBatchLookupMode ? 'Match' : 'Product alias',
        accessor: 'aliases',
        Cell: ({ cell: { value, row } }: { cell: { value: string[]; row: any } }) => (
          <TableCellProductAlias
            row={row}
            aliases={value}
            lookupName={lookupValues.name || ''}
            isBatchLookup={isBatchLookupMode}
            matchedName={
              (row?.original?.attributes?.matched_name_is_alias
                ? row.original.attributes.matched_name
                : '') || ''
            }
            requestUuid={row?.original?.attributes?.request_uuid}
          />
        ),
      },
      {
        id: 'Copy',
        Cell: ({ cell: { value, row } }: { cell: { value: any; row: any } }) => {
          return (
            <TableCellCopy
              gettingProductId={gettingProductId}
              lastAction={lastAction}
              dispatch={dispatch}
              row={row}
            />
          )
        },
      },
      {
        id: 'Edit',
        Cell: ({ cell: { value, row } }: { cell: { value: any; row: any } }) => {
          return (
            <TableCellEdit
              gettingProductId={gettingProductId}
              lastAction={lastAction}
              dispatch={dispatch}
              row={row}
            />
          )
        },
      },
      {
        id: 'Create',
        Cell: ({ cell: { value, row } }: { cell: { value: any; row: any } }) => (
          <TableCellCreate
            gettingProductId={gettingProductId}
            lastAction={lastAction}
            dispatch={dispatch}
            row={row}
            alias={
              currentBatchId && isBatchLookupMode
                ? batchLookupValues[parseInt(currentBatchId)].alias
                : ''
            }
            isBatchLookup={isBatchLookupMode}
          />
        ),
      },
    ]
  }, [
    isBulkEditMode,
    isBatchLookupMode,
    productPhotos,
    onDownloadClick,
    lookupValues.name,
    gettingProductId,
    lastAction,
    dispatch,
    currentBatchId,
    batchLookupValues,
    brandDetails,
  ])

  const data = React.useMemo(() => {
    const toReturnProducts = products.map((product: LookProductApiResponse) => ({
      ...product,
      attributes: productAttributesLinter(product.attributes),
      aliases: aliasesById[product.id] || [],
      preSelected: product.id === preSelectedId,
    }))

    if (hidingQueuedProducts) {
      return toReturnProducts.filter((eachProduct: any) => !bulkEditProductsQueue?.[eachProduct.id])
    } else {
      return toReturnProducts
    }
  }, [products, hidingQueuedProducts, aliasesById, preSelectedId, bulkEditProductsQueue])

  const handleBatchModeCreateModalClose = () => {
    setIsCreateModalOpen(false)
    props.onModalActiveChange(false)
    dispatch(setBatchLookupStage({ stage: 'review' }))
  }

  useEffect(() => {
    setIsCreateModalOpen(batchLookupStage === 'create')
    props.onModalActiveChange(batchLookupStage === 'create')
  }, [batchLookupStage, props, setIsCreateModalOpen])

  function getAlgoliaFacetFilters<FormValues>(form: FormApi<FormValues & LookupValues>) {
    const formValues = { ...form.getState().values }
    const filters = []
    if (formValues.brand) {
      filters.push(`brand:"${formValues.brand}"`)
    }
    if (formValues.amount) {
      filters.push(`amount:"${formValues.amount}"`)
    }
    if (formValues.name) {
      filters.push(`product_name:"${formValues.name}"`)
    }
    if (formValues.brandSubtype) {
      filters.push(`brand_subtype:"${formValues.brandSubtype}"`)
    }
    if (formValues.dosage) {
      filters.push(`dosage:"${formValues.dosage}"`)
    }
    return filters.join(' AND ')
  }

  return (
    <>
      <Segment padded={!isBatchLookupMode} basic>
        <Form>
          <Form.Group inline>
            <label>Lookup source:</label>
            <Form.Radio
              inline
              label="Algolia"
              value="algolia"
              checked={searchFunc === 'algolia'}
              onChange={() => {
                dispatch(setSearchFunc({ searchFunc: 'algolia' }))
                saveLocal('searchFunc', 'algolia')
              }}
            />
            <Form.Radio
              inline
              label="SQL"
              value="sql"
              checked={searchFunc === 'sql'}
              onChange={() => {
                dispatch(setSearchFunc({ searchFunc: 'sql' }))
                saveLocal('searchFunc', 'sql')
              }}
            />
          </Form.Group>
        </Form>
        <Grid columns={2} stackable>
          <Grid.Row style={{ zIndex: 1 }}>
            <Grid.Column width={13}>
              <h4>By attributes</h4>
              <FinalForm
                onSubmit={onSubmit}
                initialValues={lookupValues}
                mutators={{
                  clearProductSubType: (args, state, utils) => {
                    utils.changeValue(state, 'productSubtype', () => '')
                  },
                  resetBrandMode: (args, state, utils) => {
                    utils.changeValue(state, 'brand_mode', (oldValue) => {
                      if (utils.getIn(state, 'brand') !== 'Not Set' && oldValue === 'none') {
                        return 'strict'
                      }
                      return oldValue
                    })
                  },
                  resetNameMode: (args, state, utils) => {
                    utils.changeValue(state, 'name_mode', (oldValue) => {
                      if (utils.getIn(state, 'name') !== 'Not Set' && oldValue === 'none') {
                        return 'strict'
                      }
                      return oldValue
                    })
                  },
                }}
                render={({ handleSubmit, values, form, initialValues }) => {
                  const facetFilters = getAlgoliaFacetFilters(form)
                  const showModes = isSql && !values.aliasMode

                  return (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group>
                        <Form.Field width={10}>
                          <label>Name</label>
                          <AlgoliaAutoComplete
                            filters={''}
                            facet="product_name"
                            onChange={(val) => form.change('name', val)}
                            returnFocus={focusOnSubmitButton}
                            initialValue={initialValues.name}
                            onSubmit={form.submit}
                          />
                          {showModes && (
                            <>
                              <div className="ui checkbox radio" style={{ marginRight: '10px' }}>
                                <Field
                                  name="name_mode"
                                  component="input"
                                  type="radio"
                                  id="name-mode-strict"
                                  value="strict"
                                />
                                <label htmlFor="name-mode-strict">Exact Match</label>
                              </div>
                              <div className="ui checkbox radio">
                                <Field
                                  name="name_mode"
                                  component="input"
                                  type="radio"
                                  id="name-mode-strict"
                                  value="contains"
                                />
                                <label htmlFor="name-mode-contains">Contains</label>
                              </div>
                            </>
                          )}
                        </Form.Field>
                        <Form.Field width={6}>
                          <label>Brand</label>
                          <AlgoliaAutoComplete
                            filters={''}
                            facet="brand"
                            onChange={(value: any) => {
                              form.change('brand', value)
                              form.mutators.resetBrandMode()
                            }}
                            returnFocus={focusOnSubmitButton}
                            initialValue={initialValues.brand}
                            hasNotSet
                            onSubmit={form.submit}
                          />
                          {showModes && (
                            <>
                              <div className="ui checkbox radio" style={{ marginRight: '10px' }}>
                                <Field
                                  name="brand_mode"
                                  component="input"
                                  type="radio"
                                  id="brand-mode-strict"
                                  value="strict"
                                />
                                <label htmlFor="brand-mode-strict">Exact Match</label>
                              </div>
                              <div className="ui checkbox radio">
                                <Field
                                  name="brand_mode"
                                  component="input"
                                  type="radio"
                                  id="brand-mode-strict"
                                  value="contains"
                                />
                                <label htmlFor="brand-mode-contains">Contains</label>
                              </div>
                            </>
                          )}
                        </Form.Field>
                      </Form.Group>
                      <Form.Group>
                        <Form.Field width={4}>
                          <label>Product type</label>
                          <Field name="productType" component="select" disabled={isDirectLookup}>
                            {(props) => (
                              <select
                                style={{ height: 38 }}
                                {...props.input}
                                onKeyDown={(e) => e.preventDefault()}
                                onChange={(e) => {
                                  form.mutators.clearProductSubType()
                                  props.input.onChange(e)
                                  focusOnSubmitButton()
                                }}
                              >
                                <IdSelectionOptions options={PRODUCT_TYPE_OPTIONS} />
                              </select>
                            )}
                          </Field>
                        </Form.Field>
                        <Form.Field width={6}>
                          <label>Product subtype</label>
                          <Field name="productSubtype" style={{ height: 38 }}>
                            {(props) => (
                              <select
                                disabled={isDirectLookup}
                                onChange={(e: any) => {
                                  e.preventDefault()
                                  props.input.onChange(e.target.value)
                                  focusOnSubmitButton()
                                }}
                                onKeyDown={(e) => e.preventDefault()}
                                value={props.input.value}
                              >
                                {values.productType ? (
                                  <>
                                    <option value=""></option>
                                    <option value="Not Set">Not Set</option>
                                    <SelectionOptions
                                      options={
                                        PRODUCT_SUBTYPE_OPTIONS[
                                          PRODUCT_TYPE_KEY_BY_ID[parseInt(values.productType)]
                                        ]
                                      }
                                    />
                                  </>
                                ) : (
                                  ''
                                )}
                              </select>
                            )}
                          </Field>
                        </Form.Field>
                        <Form.Field width={6}>
                          <label>Brand subtype</label>
                          <AlgoliaAutoComplete
                            filters={facetFilters}
                            facet="brand_subtype"
                            onChange={(value: any) => {
                              form.change('brandSubtype', value)
                            }}
                            returnFocus={focusOnSubmitButton}
                            initialValue={initialValues.brandSubtype}
                            hasNotSet
                            onSubmit={form.submit}
                          />
                        </Form.Field>
                      </Form.Group>
                      <Form.Group>
                        <Form.Field width={4}>
                          <label>Lineage</label>
                          <Field name="lineage" style={{ height: 38 }}>
                            {(props) => (
                              <select
                                disabled={isDirectLookup}
                                onKeyDown={(e) => e.preventDefault()}
                                onChange={(e: any) => {
                                  e.preventDefault()
                                  props.input.onChange(e.target.value)
                                  focusOnSubmitButton()
                                }}
                                value={props.input.value}
                              >
                                <option value="" />
                                <option value="Not Set">Not Set</option>
                                <IdSelectionOptions options={LINEAGE_OPTIONS} />
                              </select>
                            )}
                          </Field>
                        </Form.Field>
                        <Form.Field width={6}>
                          <label>Dosage</label>
                          <AlgoliaAutoComplete
                            filters={facetFilters}
                            facet="dosage"
                            onChange={(value: any) => {
                              form.change('dosage', value)
                            }}
                            returnFocus={focusOnSubmitButton}
                            initialValue={initialValues.dosage}
                            hasNotSet
                            onSubmit={form.submit}
                          />
                        </Form.Field>
                        <Form.Field width={6}>
                          <label>Amount</label>
                          <AlgoliaAutoComplete
                            filters={facetFilters}
                            facet="amount"
                            onChange={(value: any) => {
                              form.change('amount', value)
                            }}
                            returnFocus={focusOnSubmitButton}
                            initialValue={initialValues.amount}
                            hasNotSet
                            onSubmit={form.submit}
                          />
                        </Form.Field>
                      </Form.Group>
                      <Form.Group>
                        {isBulkEditMode && (
                          <div className="field">
                            <div className="ui checkbox">
                              <input
                                id="input-hide-queued"
                                type="checkbox"
                                onChange={() => setHideQueuedProducts(!hideQueuedProducts)}
                              />
                              <Popup
                                content="Hide lookup results for queued products"
                                mouseEnterDelay={300}
                                trigger={
                                  <label htmlFor="input-hide-queued">Hide queue products</label>
                                }
                                offset={[-11, 0]}
                              />
                            </div>
                          </div>
                        )}
                      </Form.Group>
                      <Form.Group style={{ alignItems: 'end', marginTop: 40 }}>
                        <Button.Group color="blue" className="button icon">
                          <Button
                            primary
                            disabled={isDirectLookup}
                            type="submit"
                            onClick={handleSubmit}
                            icon={enableDeepMatcher ? 'random' : 'bullseye'}
                            content=" Lookup"
                          />
                          <Dropdown button floating>
                            <Dropdown.Menu style={{ right: -45 }}>
                              <Dropdown.Header content="Lookup enhancement" />
                              <Dropdown.Item
                                icon="random"
                                text="DeepMatcher"
                                onClick={() => {
                                  dispatch(setEnableDeepMatcher({ enableDeepMatcher: true }))
                                  saveLocal('enableDeepMatcher', 'true')
                                }}
                              />
                              <Dropdown.Item
                                icon="bullseye"
                                text="Classic"
                                onClick={() => {
                                  dispatch(setEnableDeepMatcher({ enableDeepMatcher: false }))
                                  saveLocal('enableDeepMatcher', 'false')
                                }}
                              />
                            </Dropdown.Menu>
                          </Dropdown>
                        </Button.Group>
                        {/*This button is used for returning focus after choosing an item from AlgoliaAutoComplete*/}
                        {/*It allows the enter key to be used to submit*/}
                        {/*display: none does not allow the ref to work correctly*/}
                        {/*so we make it tiny & transparent*/}
                        <button
                          ref={submitButtonRef}
                          style={{
                            width: '1px',
                            height: '1px',
                            background: 'transparent',
                            border: 'none',
                          }}
                          onKeyDown={(e) => {
                            if (e.key.toUpperCase() === 'ENTER') {
                              handleSubmit()
                            }
                          }}
                        />
                        <LookupOptionsFormGroup
                          disabled={isDirectLookup}
                          style={{
                            marginRight: 20,
                            display: 'inline-flex',
                            alignItems: 'flex-end',
                          }}
                          isBatchLookup={isBatchLookupMode}
                        ></LookupOptionsFormGroup>

                        <Button
                          disabled={isDirectLookup}
                          onClick={(e) => {
                            e.preventDefault()
                            dispatch(resetLookup())
                            form.reset()
                          }}
                          type="reset"
                        >
                          Reset
                        </Button>
                      </Form.Group>
                    </Form>
                  )
                }}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <h4>By ID</h4>
              <FinalForm
                onSubmit={(values) => {
                  if (values.productId != null) {
                    dispatch(setProducts({ products: [] }))
                    dispatch(setDirectUrl({ url: '' }))
                    dispatch(setIsLoading({ status: true }))
                    dispatch(
                      getAProduct({
                        productId: values.productId,
                        mode: 'lookup',
                      })
                    )
                  }
                }}
                initialValues={{ productId: lookupProductId }}
                render={({ handleSubmit }) => (
                  <Form onSubmit={handleSubmit}>
                    <Field name="productId" type="number">
                      {(props) => (
                        <Form.Field>
                          <label>Product ID</label>
                          <Input
                            {...props.input}
                            action
                            min="0"
                            style={{ width: '80%' }}
                            onFocus={() => setIsDirectLookup(true)}
                            onBlur={() => setIsDirectLookup(false)}
                          >
                            <input />
                            <Button icon="search" type="submit" />
                          </Input>
                        </Form.Field>
                      )}
                    </Field>
                  </Form>
                )}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
      <Segment basic id="lookup-table-segment" loading={isLoading}>
        <LookupTable
          columns={mode !== 'bulkEdit' ? defaultAndBatchLookupColumns : bulkEditLookupColumns}
          data={data}
          isBatchLookup={isBatchLookupMode}
        />
      </Segment>
      <Segment
        basic
        padded
        style={{
          display: directUrl.length > 0 && !isBatchLookupMode ? 'block' : 'none',
          paddingBottom: 0,
        }}
      >
        <Icon name="linkify" color="grey" />
        <a href={directUrl} target="new" style={{ marginRight: '10px' }}>
          {directUrl.slice(2)}
        </a>
        <CopyToClipboard
          text={directUrl.slice(2)}
          onCopy={() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          <CustomCopyButton size="mini" basic>
            Copy
          </CustomCopyButton>
        </CopyToClipboard>
        {copied ? <small style={{ color: 'green' }}> Copied</small> : null}
      </Segment>

      <ProductModalStyle
        className="product-modal"
        open={isCreateModalOpen}
        closeOnEscape={false}
        closeOnDimmerClick={false}
        onClose={handleBatchModeCreateModalClose}
      >
        <Modal.Content scrolling>
          <Products handleBatchModeCreateModalClose={handleBatchModeCreateModalClose} />
        </Modal.Content>
      </ProductModalStyle>

      <Segment
        basic
        padded
        style={{
          display: products.length > 0 && !isBatchLookupMode ? 'block' : 'none',
          paddingTop: 0,
          paddingBottom: 0,
          margin: 0,
        }}
      >
        *
        <small>
          Product clipboard fields order: Product Name, Brand, Product type, Brand subtype, Product
          subtype, Lineage, Amount, Dosage, CBD Dosage, THC Dosage, Quantity value, Quantity units,
          Description
        </small>
      </Segment>
      <ProductClipboard product={clipboardProduct} />
    </>
  )
}

export default LookupProduct
